export const COMPANY_ACCESS_DENIED_EVENT =
  "orquesti:company-access-denied" as const;

export type AppAccessErrorCode = "company_access_denied";

export type CompanyAccessDeniedDetail = {
  code: AppAccessErrorCode;
  message: string;
};

function collectErrorTexts(value: unknown, bucket: string[]) {
  if (value == null) return;

  if (typeof value === "string") {
    bucket.push(value);
    return;
  }

  if (value instanceof Error) {
    bucket.push(value.message);
    collectErrorTexts(value.cause, bucket);
    return;
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;

    for (const key of ["message", "originalError", "error", "details", "hint"]) {
      const nestedValue = record[key];
      if (typeof nestedValue === "string") {
        bucket.push(nestedValue);
      } else if (nestedValue != null) {
        collectErrorTexts(nestedValue, bucket);
      }
    }

    if ("cause" in record) {
      collectErrorTexts(record.cause, bucket);
    }
  }
}

export function isCompanyAccessDeniedError(error: unknown): boolean {
  const texts: string[] = [];
  collectErrorTexts(error, texts);

  return texts.some((text) =>
    text.toLowerCase().includes("access denied for company"),
  );
}

export function getCompanyAccessDeniedDetail(
  error: unknown,
): CompanyAccessDeniedDetail | null {
  if (!isCompanyAccessDeniedError(error)) {
    return null;
  }

  const texts: string[] = [];
  collectErrorTexts(error, texts);

  const matchedMessage =
    texts.find((text) => text.toLowerCase().includes("access denied for company")) ??
    "Access denied for company";

  return {
    code: "company_access_denied",
    message: matchedMessage,
  };
}

export function notifyCompanyAccessDenied(error: unknown) {
  if (typeof window === "undefined") return;

  const detail = getCompanyAccessDeniedDetail(error);
  if (!detail) return;

  window.dispatchEvent(
    new CustomEvent<CompanyAccessDeniedDetail>(COMPANY_ACCESS_DENIED_EVENT, {
      detail,
    }),
  );
}
